import { ProgressItem } from './ProgressCards';
import { SummedReport } from '../../../models/ReportData';
import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import { HelpBody, HelpTitle } from '../../../components/HelpText';
import { Spacer } from '../../../components';
import { ProgressCalculator } from './ReportProgressTracker';
import { findPricebookReportsByType } from '../../../data/report/helpers/pricebookReportUtils';
import { ProductTypeEnum } from '../../../models/PricebookData';
import { formatNumber, sumPropertyAcrossArrayItems } from '../../../helpers/utilityFunctions';
import { sumNestedProperty } from '../../../data/report/helpers/calculationUtils';

const getPersonalDetailsSteps = (summedReport: SummedReport): ProgressItem[] => {
  return [1, 2, 3].map(step => ({
    label: `Reached step ${step}`,
    value: sumNestedProperty(summedReport, `personalDetailsStep${step}Count`),
  }));
};

export const progressTracker3Nickels: ProgressCalculator = summedReport => {
  const personalDetailsSteps = getPersonalDetailsSteps(summedReport);
  const reports3nickels = findPricebookReportsByType(summedReport, [
    ProductTypeEnum.Nickels,
    ProductTypeEnum.NickelsU,
  ]);

  return {
    title: '3Nickels onboarding',
    totalUsers: sumPropertyAcrossArrayItems(reports3nickels, 'activeUserCount'),
    notStarted: summedReport.notStartedNickels ?? 0,
    inProgress: [
      {
        label: 'Agreed to terms of service',
        value: sumPropertyAcrossArrayItems(reports3nickels, 'acceptedTosCount'),
      },
      {
        label: 'Started personal details',
        value: personalDetailsSteps[0].value,
        expandableComponent: (
          <PersonalDetailsExpandable personalDetailsSteps={personalDetailsSteps} />
        ),
      },
      {
        label: 'Finished personal details',
        value: sumPropertyAcrossArrayItems(reports3nickels, 'personalDetailsCompleteCount'),
      },
      {
        label: 'Finished account details',
        value: sumPropertyAcrossArrayItems(reports3nickels, 'accountDetailsCompleteCount'),
      },
      {
        label: 'Finished budget details',
        value: sumPropertyAcrossArrayItems(reports3nickels, 'budgetDetailsCompletedCount'),
      },
    ],
    completed: sumPropertyAcrossArrayItems(reports3nickels, 'onboardingCompletedCount'),
    helpText: '3Nickels active users',
    helpContent: <HelpText3nickels />,
  };
};

export const progressTrackerBundled: ProgressCalculator = summedReport => {
  const personalDetailsSteps = getPersonalDetailsSteps(summedReport);
  const reportsBundled = findPricebookReportsByType(summedReport, [ProductTypeEnum.Bundled]);

  return {
    title: 'Bundled onboarding',
    totalUsers: sumPropertyAcrossArrayItems(reportsBundled, 'activeUserCount'),
    notStarted: summedReport.notStartedBundled ?? 0,
    inProgress: [
      {
        label: 'Agreed to terms of service',
        value: sumPropertyAcrossArrayItems(reportsBundled, 'acceptedTosCount'),
      },
      {
        label: 'Started personal details',
        value: personalDetailsSteps[0].value,
        expandableComponent: (
          <PersonalDetailsExpandable personalDetailsSteps={personalDetailsSteps} />
        ),
      },
      {
        label: 'Finished personal details',
        value: sumPropertyAcrossArrayItems(reportsBundled, 'personalDetailsCompleteCount'),
      },
      {
        label: 'Finished account details',
        value: sumPropertyAcrossArrayItems(reportsBundled, 'accountDetailsCompleteCount'),
      },
      {
        label: 'Finished budget details',
        value: sumPropertyAcrossArrayItems(reportsBundled, 'budgetDetailsCompletedCount'),
      },
    ],
    completed: sumPropertyAcrossArrayItems(reportsBundled, 'onboardingCompletedCount'),
    helpText: 'Bundled active users',
    helpContent: null,
  };
};

const ExpandedProgressCard = ({ item, gridColumn }: { item: ProgressItem; gridColumn: string }) => {
  return (
    <Grid
      sx={{ gridColumn }}
      display='flex'
      flexDirection='column'
      whiteSpace='nowrap'
      padding='16px'>
      <Typography variant='p12SemiBold' color='secondary.main' lineHeight={1.5}>
        {item.label}
      </Typography>
      <Typography variant='p20' color='secondary.main'>
        {formatNumber(item.value)}
      </Typography>
    </Grid>
  );
};

const PersonalDetailsExpandable = ({
  personalDetailsSteps,
}: {
  personalDetailsSteps: ProgressItem[];
}) => {
  const formattedNumber = formatNumber(personalDetailsSteps[0].value);
  const digitCount = formattedNumber.replace(/,/g, '').length;
  const commaCount = (formattedNumber.match(/,/g) || []).length;

  const baseMargin = 20;
  const digitWidth = 11;
  const commaWidth = 7;
  const marginLeft = baseMargin + digitCount * digitWidth + commaCount * commaWidth;

  return (
    <Box sx={{ display: 'contents' }}>
      <Box
        sx={{
          gridColumn: '2 / 3',
          display: 'flex',
          marginLeft: `${marginLeft}px`,
        }}>
        {/* expandable arrow */}
        <Box
          width='0px'
          style={{
            borderLeft: '18px solid transparent',
            borderRight: '18px solid transparent',
            borderBottom: '18px solid #fff',
          }}
        />
      </Box>

      <Card
        sx={{
          gridColumn: '1 / -1',
          display: 'grid',
          gridTemplateColumns: 'subgrid',
        }}
        variant='ghost'
        color='tertiary'>
        <CardContent sx={{ display: 'contents' }}>
          <ExpandedProgressCard item={personalDetailsSteps[0]} gridColumn='1 / 2' />
          <ExpandedProgressCard item={personalDetailsSteps[1]} gridColumn='3 / 4' />
          <ExpandedProgressCard item={personalDetailsSteps[2]} gridColumn='5 / 6' />
        </CardContent>
      </Card>
    </Box>
  );
};

const HelpText3nickels = () => {
  return (
    <Grid>
      <HelpTitle>3Nickels active users</HelpTitle>
      <Spacer height='xxs' />
      <HelpBody>
        Count of users connected to this organization who have an active license for any 3Nickels
        subscription.
      </HelpBody>
    </Grid>
  );
};
